import axios, { AxiosInstance } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Customer,
  Questionnaire,
  CustomerResponse,
  ResponseSummary,
  SectionCommentData,
  SectionComment,
  AiSuggestion,
  LLMConfig,
  ManagedSystem
} from '../types';
import {
  SurveyTemplateSummary,
  SurveyTemplate,
  SurveySummary,
  Survey,
  SurveyResponse,
  SurveyResponseItem,
  SurveySummaryStats,
  SurveyComment,
  EmailGroup,
  EmailGroupSummary,
  ExcelImportResult
} from '../types/survey';

const API_URL = process.env.REACT_APP_API_URL || (window.location.protocol === 'https:' ? '/api' : `http://${window.location.hostname}:3010/api`);

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      async (config) => {
        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Error fetching auth session:', error);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  // Customer endpoints
  async getCustomers(): Promise<Customer[]> {
    const response = await this.client.get('/customers');
    return response.data?.customers || [];
  }

  async getCustomer(customerId: string): Promise<Customer> {
    const response = await this.client.get(`/customers/${customerId}`);
    return response.data;
  }

  async createCustomer(customerName: string, email?: string, projectName?: string): Promise<Customer> {
    const response = await this.client.post('/customers', { customerName, email, projectName });
    return response.data;
  }

  async updateCustomer(customerId: string, data: { customerName?: string; email?: string; projectName?: string }): Promise<Customer> {
    const response = await this.client.put(`/customers/${customerId}`, data);
    return response.data;
  }

  async deleteCustomer(customerId: string): Promise<void> {
    await this.client.delete(`/customers/${customerId}`);
  }

  async archiveCustomer(customerId: string): Promise<Customer> {
    const response = await this.client.put(`/customers/${customerId}/archive`);
    return response.data;
  }

  async restoreCustomer(customerId: string): Promise<Customer> {
    const response = await this.client.put(`/customers/${customerId}/restore`);
    return response.data;
  }

  async getArchivedCustomers(): Promise<Customer[]> {
    const response = await this.client.get('/customers?archived=true');
    return response.data?.customers || [];
  }

  async sendQuestionnaire(customerId: string, questionnaireId: string, questionnaireTitle: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post(`/customers/${customerId}/send-questionnaire`, {
      questionnaireId,
      questionnaireTitle,
      baseUrl: window.location.origin,
    });
    return response.data;
  }

  // Questionnaire endpoints
  async getQuestionnaires(): Promise<{ questionnaireId: string; title: string; fileName: string }[]> {
    const response = await this.client.get('/questionnaires');
    return response.data?.questionnaires || [];
  }

  async getQuestionnaire(questionnaireId: string): Promise<Questionnaire> {
    const response = await this.client.get(`/questionnaires/${questionnaireId}`);
    return response.data;
  }

  // Response endpoints
  async createResponse(data: {
    customerId: string;
    customerName: string;
    questionnaireId: string;
    responses: any[];
  }): Promise<CustomerResponse> {
    const response = await this.client.post('/responses', data);
    return response.data;
  }

  async getResponses(customerId: string): Promise<ResponseSummary[]> {
    const response = await this.client.get(`/responses?customerId=${customerId}`);
    return response.data?.responses || [];
  }

  async getResponse(responseId: string, customerId: string): Promise<CustomerResponse> {
    const response = await this.client.get(`/responses/${responseId}?customerId=${customerId}`);
    return response.data;
  }

  async updateResponse(
    responseId: string,
    customerId: string,
    data: {
      customerId: string;
      customerName: string;
      questionnaireId: string;
      responses: any[];
    }
  ): Promise<CustomerResponse> {
    const response = await this.client.put(`/responses/${responseId}?customerId=${customerId}`, data);
    return response.data;
  }

  async deleteResponse(responseId: string, customerId: string, permanent: boolean = false): Promise<void> {
    await this.client.delete(`/responses/${responseId}?customerId=${customerId}${permanent ? '&permanent=true' : ''}`);
  }

  async archiveResponse(responseId: string, customerId: string): Promise<void> {
    await this.client.put(`/responses/${responseId}/archive?customerId=${customerId}`);
  }

  async restoreResponse(responseId: string, customerId: string): Promise<void> {
    await this.client.put(`/responses/${responseId}/restore?customerId=${customerId}`);
  }

  async getArchivedResponses(customerId: string): Promise<ResponseSummary[]> {
    const response = await this.client.get(`/responses/archived?customerId=${customerId}`);
    return response.data?.responses || [];
  }

  async bulkArchiveResponses(customerId: string, responseIds: string[]): Promise<void> {
    await this.client.post('/responses/bulk-archive', { customerId, responseIds });
  }

  async bulkDeleteResponses(customerId: string, responseIds: string[]): Promise<void> {
    await this.client.post('/responses/bulk-delete', { customerId, responseIds });
  }

  // Survey Template endpoints
  async getSurveyTemplates(): Promise<SurveyTemplateSummary[]> {
    const response = await this.client.get('/survey-templates');
    return response.data?.templates || [];
  }

  async getSurveyTemplate(templateId: string): Promise<SurveyTemplate> {
    const response = await this.client.get(`/survey-templates/${templateId}`);
    return response.data;
  }

  async uploadSurveyTemplate(file: File, title: string, type: string): Promise<SurveyTemplate> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('type', type);
    const response = await this.client.post('/survey-templates/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async updateSurveyTemplate(templateId: string, data: {
    title?: string;
    type?: string;
    questions?: Array<{
      category: string;
      question: string;
      purpose: string;
      defaultAnswer: string;
      notes: string;
    }>;
  }): Promise<SurveyTemplate> {
    const response = await this.client.put(`/survey-templates/${templateId}`, data);
    return response.data;
  }

  async deleteSurveyTemplate(templateId: string): Promise<void> {
    await this.client.delete(`/survey-templates/${templateId}`);
  }

  // Survey endpoints
  async getSurveys(archived: boolean = false): Promise<SurveySummary[]> {
    const response = await this.client.get(`/surveys?archived=${archived}`);
    return response.data?.surveys || [];
  }

  async getSurvey(surveyId: string): Promise<Survey> {
    const response = await this.client.get(`/surveys/${surveyId}`);
    return response.data;
  }

  async createSurvey(data: {
    templateId: string;
    title: string;
    respondents: { email: string; name: string }[];
    expiresAt?: string;
    aiEnabled?: boolean;
  }): Promise<Survey> {
    const response = await this.client.post('/surveys', data);
    return response.data;
  }

  async sendSurvey(surveyId: string): Promise<Survey> {
    const response = await this.client.post(`/surveys/${surveyId}/send`, {
      baseUrl: window.location.origin,
    });
    return response.data;
  }

  async closeSurvey(surveyId: string): Promise<Survey> {
    const response = await this.client.put(`/surveys/${surveyId}/close`);
    return response.data;
  }

  async deleteSurvey(surveyId: string): Promise<void> {
    await this.client.delete(`/surveys/${surveyId}`);
  }

  async updateSurvey(surveyId: string, data: { title?: string; expiresAt?: string; aiEnabled?: boolean }): Promise<Survey> {
    const response = await this.client.put(`/surveys/${surveyId}`, data);
    return response.data;
  }

  async archiveSurvey(surveyId: string, archived: boolean): Promise<Survey> {
    const response = await this.client.put(`/surveys/${surveyId}/archive`, { archived });
    return response.data;
  }

  async toggleSurveyVisibility(surveyId: string, hidden: boolean): Promise<Survey> {
    const response = await this.client.put(`/surveys/${surveyId}/visibility`, { hidden });
    return response.data;
  }

  async updateSurveyRemark(surveyId: string, remark: string): Promise<Survey> {
    const response = await this.client.put(`/surveys/${surveyId}/remark`, { remark });
    return response.data;
  }

  // Create survey with pre-filled responses (Excel import)
  async createSurveyWithPrefilledResponse(data: {
    title: string;
    respondent: { email: string; name: string };
    responses: SurveyResponseItem[];
    expiresAt?: string;
    aiEnabled?: boolean;
    sourceFile?: { name: string; data: string };
  }): Promise<ExcelImportResult> {
    const response = await this.client.post('/surveys/import-with-responses', data);
    return response.data;
  }

  // Get source file download URL
  async getSurveySourceFile(surveyId: string): Promise<{ url: string; fileName: string }> {
    const response = await this.client.get(`/surveys/${surveyId}/source-file`);
    return response.data;
  }

  async getSurveyComments(surveyId: string): Promise<SurveyComment[]> {
    const response = await this.client.get(`/surveys/${surveyId}/comments`);
    return response.data?.comments || [];
  }

  async addSurveyComment(surveyId: string, text: string, author: string): Promise<SurveyComment> {
    const response = await this.client.post(`/surveys/${surveyId}/comments`, { text, author });
    return response.data;
  }

  async deleteSurveyComment(surveyId: string, commentId: string): Promise<void> {
    await this.client.delete(`/surveys/${surveyId}/comments/${commentId}`);
  }

  // Survey Results
  async getSurveyResults(surveyId: string): Promise<{ survey: Survey; results: SurveyResponse[]; total: number }> {
    const response = await this.client.get(`/surveys/${surveyId}/results`);
    return response.data;
  }

  async getSurveySummary(surveyId: string): Promise<SurveySummaryStats> {
    const response = await this.client.get(`/surveys/${surveyId}/summary`);
    return response.data;
  }

  getSurveyExportUrl(surveyId: string, format: 'filled' | 'all' | 'individual', email?: string, fileType?: 'xlsx' | 'csv'): string {
    let base = `${API_URL}/surveys/${surveyId}/export?format=${format}`;
    if (fileType) base += `&fileType=${fileType}`;
    return email ? `${base}&email=${encodeURIComponent(email)}` : base;
  }

  // Email Group endpoints
  async getEmailGroups(): Promise<EmailGroupSummary[]> {
    const response = await this.client.get('/email-groups');
    return response.data?.groups || [];
  }

  async getEmailGroup(groupId: string): Promise<EmailGroup> {
    const response = await this.client.get(`/email-groups/${groupId}`);
    return response.data;
  }

  async createEmailGroup(data: { name: string; description?: string; members?: { email: string; name: string }[] }): Promise<EmailGroup> {
    const response = await this.client.post('/email-groups', data);
    return response.data;
  }

  async updateEmailGroup(groupId: string, data: { name?: string; description?: string; members?: { email: string; name: string }[] }): Promise<EmailGroup> {
    const response = await this.client.put(`/email-groups/${groupId}`, data);
    return response.data;
  }

  async deleteEmailGroup(groupId: string): Promise<void> {
    await this.client.delete(`/email-groups/${groupId}`);
  }

  // Customer-Survey Linking
  async getCustomerSurveys(customerId: string): Promise<SurveySummary[]> {
    const response = await this.client.get(`/customers/${customerId}/surveys`);
    return response.data?.surveys || [];
  }

  async linkSurveyToCustomer(customerId: string, surveyId: string): Promise<void> {
    await this.client.post(`/customers/${customerId}/surveys`, { surveyId });
  }

  async unlinkSurveyFromCustomer(customerId: string, surveyId: string): Promise<void> {
    await this.client.delete(`/customers/${customerId}/surveys/${surveyId}`);
  }

  async importEmailGroupCsv(groupId: string, file: File): Promise<EmailGroup> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post(`/email-groups/${groupId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // SES Settings endpoints
  async getSesSettings(): Promise<any> {
    const response = await this.client.get('/settings/ses');
    return response.data;
  }

  async updateSesSettings(data: { sender?: string; enabled?: boolean }): Promise<any> {
    const response = await this.client.put('/settings/ses', data);
    return response.data;
  }

  async testSes(to: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/settings/ses/test', { to });
    return response.data;
  }

  // Section Comments endpoints
  async getSectionComments(questionnaireId: string, category: string): Promise<SectionCommentData> {
    const response = await this.client.get(`/section-comments/${encodeURIComponent(questionnaireId)}/${encodeURIComponent(category)}`);
    return response.data;
  }

  async addSectionComment(questionnaireId: string, category: string, comment: {
    text: string;
    type: 'comment' | 'note' | 'suggestion';
    author: string;
    priority?: string;
    tags?: string[];
    attachments?: { type: string; url: string; name: string; title?: string }[];
  }): Promise<SectionComment> {
    const response = await this.client.post(`/section-comments/${encodeURIComponent(questionnaireId)}/${encodeURIComponent(category)}`, comment);
    return response.data;
  }

  async updateSectionComment(questionnaireId: string, category: string, commentId: string, data: {
    text?: string;
    priority?: string;
    tags?: string[];
  }): Promise<SectionComment> {
    const response = await this.client.put(`/section-comments/${encodeURIComponent(questionnaireId)}/${encodeURIComponent(category)}/${commentId}`, data);
    return response.data;
  }

  async deleteSectionComment(questionnaireId: string, category: string, commentId: string): Promise<void> {
    await this.client.delete(`/section-comments/${encodeURIComponent(questionnaireId)}/${encodeURIComponent(category)}/${commentId}`);
  }

  async generateAiSuggestions(questionnaireId: string, category: string, questions: string[]): Promise<AiSuggestion[]> {
    const response = await this.client.post(`/section-comments/${encodeURIComponent(questionnaireId)}/${encodeURIComponent(category)}/ai-suggest`, { questions });
    return response.data?.suggestions || [];
  }

  async uploadCommentImage(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('image', file);
    const response = await this.client.post('/section-comments/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // LLM Dimension Analysis
  async analyzeDimensions(dimensions: { [key: string]: { problemStatement: string[] } }, lang: 'JP' | 'EN'): Promise<{
    success: boolean;
    results: { [key: string]: { keyRisks: string[]; successCriteria: string[]; error?: string } };
  }> {
    const response = await this.client.post('/llm/analyze-dimensions', { dimensions, lang });
    return response.data;
  }

  // LLM Maturity Evaluation
  async evaluateMaturity(respondentName: string, categoryAnswers: { [key: string]: Array<{ no: number; question: string; answer: string }> }, lang: string): Promise<{
    success: boolean;
    evaluation: {
      overallScore: number;
      overallReason: string;
      categoryScores: { [key: string]: { score: number; reason: string; strengths: string[]; improvements: string[] } };
      recommendations: string[];
    };
  }> {
    const response = await this.client.post('/llm/evaluate-maturity', { respondentName, categoryAnswers, lang });
    return response.data;
  }

  // LLM Configurations
  async getLLMConfigurations(): Promise<LLMConfig[]> {
    const response = await this.client.get('/llm/configurations');
    // Map backend field names to frontend field names
    const configs = response.data?.configurations || (Array.isArray(response.data) ? response.data : []);
    if (!Array.isArray(configs)) return [];
    return configs.map((c: any) => ({
      id: String(c.id),
      name: c.name,
      provider: c.provider,
      model: c.model_name || c.model,
      apiUrl: c.api_url || c.apiUrl,
      apiKey: c.api_key || c.apiKey || '',
      isActive: c.is_active ?? c.isActive ?? false,
      createdAt: c.createdAt || c.created_at || '',
      updatedAt: c.updatedAt || c.updated_at
    }));
  }

  async createLLMConfiguration(data: Partial<LLMConfig>): Promise<LLMConfig> {
    // Map frontend field names to backend field names
    const payload = {
      name: data.name,
      provider: data.provider,
      model_name: data.model,
      api_url: data.apiUrl,
      api_key: data.apiKey
    };
    const response = await this.client.post('/llm/configurations', payload);
    const c = response.data;
    return {
      id: String(c.id),
      name: c.name,
      provider: c.provider,
      model: c.model_name || c.model,
      apiUrl: c.api_url || c.apiUrl,
      apiKey: c.api_key || c.apiKey || '',
      isActive: c.is_active ?? c.isActive ?? false,
      createdAt: c.createdAt || c.created_at || '',
      updatedAt: c.updatedAt || c.updated_at
    };
  }

  async updateLLMConfiguration(id: string, data: Partial<LLMConfig>): Promise<LLMConfig> {
    // Map frontend field names to backend field names
    const payload = {
      name: data.name,
      provider: data.provider,
      model_name: data.model,
      api_url: data.apiUrl,
      api_key: data.apiKey
    };
    const response = await this.client.put(`/llm/configurations/${id}`, payload);
    const c = response.data;
    return {
      id: String(c.id),
      name: c.name,
      provider: c.provider,
      model: c.model_name || c.model,
      apiUrl: c.api_url || c.apiUrl,
      apiKey: c.api_key || c.apiKey || '',
      isActive: c.is_active ?? c.isActive ?? false,
      createdAt: c.createdAt || c.created_at || '',
      updatedAt: c.updatedAt || c.updated_at
    };
  }

  async deleteLLMConfiguration(id: string): Promise<void> {
    await this.client.delete(`/llm/configurations/${id}`);
  }

  async activateLLMConfiguration(id: string): Promise<LLMConfig> {
    const response = await this.client.post(`/llm/activate/${id}`);
    const c = response.data;
    return {
      id: String(c.id),
      name: c.name,
      provider: c.provider,
      model: c.model_name || c.model,
      apiUrl: c.api_url || c.apiUrl,
      apiKey: c.api_key || c.apiKey || '',
      isActive: c.is_active ?? c.isActive ?? false,
      createdAt: c.createdAt || c.created_at || '',
      updatedAt: c.updatedAt || c.updated_at
    };
  }

  async testLLMConfiguration(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post(`/llm/test/${id}`);
    return response.data;
  }

  // Managed Systems (Site Management)
  async getManagedSystems(): Promise<ManagedSystem[]> {
    const response = await this.client.get('/admin/managed-systems');
    // Handle both array and object responses
    const data = response.data?.systems || response.data;
    return Array.isArray(data) ? data : [];
  }

  async createManagedSystem(data: Partial<ManagedSystem>): Promise<ManagedSystem> {
    const response = await this.client.post('/admin/managed-systems', data);
    return response.data;
  }

  async updateManagedSystem(id: string, data: Partial<ManagedSystem>): Promise<ManagedSystem> {
    const response = await this.client.put(`/admin/managed-systems/${id}`, data);
    return response.data;
  }

  async deleteManagedSystem(id: string): Promise<void> {
    await this.client.delete(`/admin/managed-systems/${id}`);
  }

  async deployManagedSystem(id: string): Promise<{ success: boolean; log: string }> {
    const response = await this.client.post(`/admin/managed-systems/${id}/deploy`);
    return response.data;
  }

  async scaffoldManagedSystem(data: { title: string; id: string; port?: number; description?: string }): Promise<ManagedSystem> {
    const response = await this.client.post('/admin/managed-systems/scaffold', data);
    return response.data;
  }

  // EA Notebook endpoints
  async getEANotebooks(): Promise<any[]> {
    try {
      const response = await this.client.get('/ea-notebooks');
      const notebooks = response.data?.notebooks || response.data;
      return Array.isArray(notebooks) ? notebooks : [];
    } catch (err) {
      console.error('Failed to get notebooks:', err);
      return [];
    }
  }

  async getEANotebook(notebookId: string): Promise<any> {
    const response = await this.client.get(`/ea-notebooks/${notebookId}`);
    return response.data;
  }

  async createEANotebook(data: { title: string; description?: string }): Promise<any> {
    const response = await this.client.post('/ea-notebooks', data);
    return response.data;
  }

  async updateEANotebook(notebookId: string, data: { title?: string; description?: string }): Promise<any> {
    const response = await this.client.put(`/ea-notebooks/${notebookId}`, data);
    return response.data;
  }

  async deleteEANotebook(notebookId: string): Promise<void> {
    await this.client.delete(`/ea-notebooks/${notebookId}`);
  }

  async getNotebookSources(notebookId: string): Promise<any[]> {
    try {
      const response = await this.client.get(`/ea-notebooks/${notebookId}/sources`);
      const sources = response.data?.sources || response.data;
      return Array.isArray(sources) ? sources : [];
    } catch (err) {
      console.error('Failed to get sources:', err);
      return [];
    }
  }

  async uploadNotebookSource(notebookId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post(`/ea-notebooks/${notebookId}/sources/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async addNotebookUrl(notebookId: string, url: string): Promise<any> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/sources/url`, { url });
    return response.data;
  }

  async addNotebookText(notebookId: string, text: string, name?: string): Promise<any> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/sources/text`, { text, name });
    return response.data;
  }

  async deleteNotebookSource(notebookId: string, sourceId: string): Promise<void> {
    await this.client.delete(`/ea-notebooks/${notebookId}/sources/${sourceId}`);
  }

  async analyzeNotebookSource(notebookId: string, sourceId: string): Promise<any> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/sources/${sourceId}/analyze`);
    return response.data;
  }

  async analyzeNotebookSources(notebookId: string): Promise<any> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/analyze`);
    return response.data;
  }

  async chatWithNotebook(notebookId: string, message: string): Promise<{ answer: string }> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/chat`, { message });
    return response.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
